export type ProjectCategory = 'production' | 'research' | 'open-source';

export type ProjectIconType =
  | 'webtoon'
  | 'health'
  | 'tts'
  | 'voice'
  | 'legal'
  | 'vision'
  | 'research'
  | 'detection'
  | 'segmentation'
  | 'classification'
  | 'medical'
  | 'gan'
  | 'accessibility'
  | 'default';

export type ProjectLinkType = 'github' | 'scholar' | 'kaggle' | 'demo' | 'play';

export interface ProjectLink {
  type: ProjectLinkType;
  href: string;
}

export interface ProjectData {
  title: string;
  description: string;
  tech: string[];
  links: ProjectLink[];
  category: ProjectCategory;
  /** When set, renders the themed ProjectIcon. When omitted, the generic folder icon is used. */
  icon?: ProjectIconType;
}

export const projects: ProjectData[] = [
  {
    title: 'Webtoon Engine - SDXL LoRA Evaluation (ChiwocheonSoft)',
    description:
      'Built a blind-review SDXL LoRA evaluation harness over 24 unseen prompts covering w15 production best/step_5888 and deterministic baselines, delivering 48-image sets plus WAI150 comparison reports and Model A–D decks with coworker qualitative stats to pick the production LoRA. Integrated WD14 tagger into the production i2i workflow so detection tags auto-merge into positive prompts with Korean operator logs, preventing prompt merge failures while improving prompt coverage.',
    tech: ['Webtoon Engine', 'SDXL', 'LoRA', 'WD14 Tagger', 'Python', 'Docker'],
    links: [{ type: 'github', href: 'https://github.com/chiwoocheon001/ComfyUI-Server' }],
    category: 'production',
    icon: 'webtoon',
  },
  {
    title: 'Multi-Character Webtoon Image Generation (ChiwocheonSoft)',
    description:
      'Ran GPU0/GPU1 50_images production training (~5h05m) reaching composite 0.7301 at step 5,152 and tightened early stopping (patience 5, min_delta 0.001) with nohup-protected launchers. Hardened GT validation at 1024/1536 with CLIP+SSIM 70/30 weighting, face-crop debug exports, and face-consistency experiments (YOLO+ResNet50, SSIM/LPIPS/CV) published for reproducible benchmarking.',
    tech: [
      'PyTorch',
      'Webtoon Multi Character Image Generation',
      'SDXL',
      'Webtoon Engine API',
      'CLIP',
      'LPIPS',
      'YOLO',
      'Docker',
    ],
    links: [{ type: 'github', href: 'https://github.com/chiwoocheon001/kohyass-experiments' }],
    category: 'production',
    icon: 'webtoon',
  },
  {
    title: 'Korea Visa Points Assistant - Production Telegram Bot',
    description:
      'Built and deployed a production Telegram bot (@korean_visas_bot) that scores eligibility across three official Korean visa categories (F-2-7, D-10, E-7-4) under the Korean Immigration Service points system. Engineered per-visa hard-disqualifier rules (e.g., F-2-7 ≥3M KRW criminal/immigration fines, E-7-4 income/language sub-minimums) and shipped 14 production-hardening fixes uncovered in pre-launch review. Designed a fault-tolerant Google Sheets logging layer via Service Account auth with a JSONL replay buffer for failed writes, markdown-escaped user input, and PIPA/GDPR consent flow with /privacy and /delete commands. Deployed to Oracle Cloud Free Tier (ARM Ampere A1) as a systemd service with auto-restart, journal logging, and zero-cost 24/7 uptime.',
    tech: [
      'Python 3.10+',
      'python-telegram-bot 21.6',
      'Google Sheets API',
      'Service Account',
      'Oracle Cloud',
      'systemd',
      'asyncio',
      'PIPA / GDPR',
    ],
    links: [
      { type: 'demo', href: 'https://t.me/korean_visas_bot' },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/KoreanVisaAssistant' },
    ],
    category: 'production',
  },
  {
    title: 'AI Health Trainer - Real-time Pose Estimation with Voice Feedback',
    description:
      "Launched web application on Hugging Face Spaces serving 300+ monthly users with real-time pose estimation (95%+ accuracy) across 5 exercise types (squats, bench press, deadlifts, overhead press, pull-ups) with <120ms latency. Integrated neural TTS voice feedback system (Piper TTS) for hands-free coaching, improving user engagement by 43% with multimodal feedback (visual + audio). Engineered resolution-adaptive UI ensuring professional appearance across 480p-4K videos, resolving 11 critical bugs for zero-defect deployment. Implemented privacy-preserving face anonymization achieving 100% privacy protection.",
    tech: ['MediaPipe', 'OpenCV', 'Piper TTS', 'Gradio', 'Python', 'Docker'],
    links: [{ type: 'demo', href: 'https://huggingface.co/spaces/bekhzod-olimov/AIHealthTrainer' }],
    category: 'production',
    icon: 'health',
  },
  {
    title: 'VISIONARY v1 - AI Vision Assistant for Visually Impaired',
    description:
      'Developed real-time AI vision assistance system achieving 2,188 object detections in 10 seconds (2,000+ objects/minute) with professional color-coded visualization, enabling visually impaired individuals to navigate safely through crowded environments. Engineered multi-modal detection pipeline combining YOLOv11 (95%+ accuracy) with adaptive focal length distance estimation (±2m precision) and three-zone spatial positioning (left/center/right), processing Full HD and 4K video streams at 9-16 FPS on CPU. Implemented professional visualization with 7-category color-coded bounding boxes and HUD overlay for enhanced accessibility.',
    tech: ['YOLOv11', 'PyTorch', 'OpenCV', 'Object Detection', 'Distance Estimation', 'Accessibility'],
    links: [
      {
        type: 'demo',
        href: 'https://www.linkedin.com/posts/bekhzod-olimov-phd-33059bb1_ai-accessibility-assistivetechnology-activity-7395696005951873024-shXu',
      },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/ObjectDetection4VisuallyImpaired' },
    ],
    category: 'production',
    icon: 'accessibility',
  },
  {
    title: 'Sehro - Uzbek TTS System',
    description:
      "Launched production-ready Uzbek text-to-speech system with HTTPS deployment, monitoring analytics, and 24/7 availability serving 100+ daily users with <3s latency. Achieved 81.11% consistency score through VITS fine-tuning and rigorous 8-checkpoint evaluation across 12 audio quality metrics. Deployed secure production infrastructure with Let's Encrypt SSL certificate, Nginx reverse proxy, systemd auto-restart service, and comprehensive usage monitoring dashboard tracking visitor analytics and TTS generations in real-time.",
    tech: ['PyTorch', 'VITS', 'Gradio', 'Docker', 'Nginx', "Let's Encrypt", 'systemd'],
    links: [{ type: 'demo', href: 'https://sehrotts.duckdns.org/' }],
    category: 'production',
    icon: 'tts',
  },
  {
    title: 'UzbekVoiceAgent - Multilingual Voice AI System',
    description:
      'Launched comprehensive Uzbek/Russian/Karakalpak voice assistant serving 500+ daily users, integrating STT (NeMo), TTS (VITS2, 430K steps), and LLM (Llama-3.1-8B). Engineered hybrid 3-layer architecture achieving 600× speedup (60ms→0.1ms) and 95% accuracy across 585 Q&A entries. Optimized v3 system reducing LLM calls by 47%, embedding time by 67%, and cost by 57% per 1K queries.',
    tech: ['Python', 'NeMo', 'VITS2', 'Llama-3.1', 'FAISS', 'FastAPI', 'Docker'],
    links: [
      {
        type: 'demo',
        href: 'https://www.linkedin.com/posts/bekhzod-olimov-phd-33059bb1_ai-speechrecognition-naturallanguageprocessing-activity-7359483392368807936-mkeS',
      },
    ],
    category: 'production',
    icon: 'voice',
  },
  {
    title: 'LexiAI-UZ - Legal Document AI',
    description:
      'Deployed production-grade multilingual RAG system serving 300+ users with 10K+ monthly queries, achieving 91% QA accuracy on 25K+ legal documents and reducing research time by 73%. Optimized inference by 2.9× (1.77s→0.61s per query) through SDPA/Flash Attention and achieved 25.6× faster first-query response (15.6s→0.61s) via model persistence. Engineered production infrastructure with systemd service management, auto-restart capabilities, and language-adaptive Gradio interface handling 100K+ token contexts with multi-format support (PDF/DOCX/XLSX/images).',
    tech: ['PyTorch 2.9', 'Alloma-3B', 'SDPA/Flash', 'Tesseract', 'FAISS', 'A6000 GPU'],
    links: [
      {
        type: 'demo',
        href: 'https://www.linkedin.com/posts/bekhzod-olimov-phd-33059bb1_lexiai-suniyintellekt-huquqiytexnologiyalar-activity-7370718096673878016-vjhR',
      },
    ],
    category: 'production',
    icon: 'legal',
  },
  {
    title: 'Webtoon Engine Inference API - FastAPI REST Service',
    description:
      'Developed FastAPI REST service achieving <5s generation time (768×768) with 99.5% uptime, eliminating Webtoon Engine server dependency and reducing deployment time by 96% (2 hours→5 minutes). Enabled Docker/Makefile dual-mode deployment for flexible infrastructure setup. Streamlined image generation workflows for production environments with comprehensive API documentation.',
    tech: ['FastAPI', 'Webtoon Engine', 'Docker', 'REST API', 'Python'],
    links: [{ type: 'github', href: 'https://github.com/chiwoocheon001/ComfyUI-Server' }],
    category: 'production',
    icon: 'gan',
  },
  {
    title: 'Cervical Cancer Detection Platform (Rinorbit)',
    description:
      'Led 4-person AI team deploying cervical cancer detection platform to 3 clinical sites processing 1,000+ daily images (94.3% accuracy, 89.7% mIoU), reducing pathologist review time by 47%. Slashed inference latency by 73% (450ms→120ms) via ONNX/TensorRT optimization and enhanced segmentation precision by 28% through SAM2.1 deployment. Generated 50K+ synthetic training images via GAN pipeline, reducing cross-device variance by 35%.',
    tech: ['PyTorch', 'SAM2.1', 'YOLO', 'TensorRT', 'GANs', 'FastAPI'],
    links: [{ type: 'demo', href: 'https://www.rinorbit.com/' }],
    category: 'production',
    icon: 'medical',
  },
  {
    title: 'Multiple Commercial AI Systems (Vitasoft)',
    description:
      'Delivered 5 commercial AI systems: Korea Customs counterfeit detection (91% accuracy, 5K+ daily queries), GPARTS visual recommendation (+33% conversions), and citywide crowd safety monitoring (92% accuracy). Accelerated PoC delivery by 80% through 400+ Kaggle projects and architected Docker-based ML infrastructure achieving 99.5% uptime across 8 concurrent systems. Launched multilingual OCR/NLP pipelines serving 15K+ users with 82% answer relevance.',
    tech: ['PyTorch', 'YOLO', 'Transformers', 'FastAPI', 'MLflow', 'FAISS'],
    links: [{ type: 'demo', href: 'http://www.vitasoft.co.kr/' }],
    category: 'production',
    icon: 'vision',
  },
  {
    title: 'Image Classification Research',
    description:
      'Conducted extensive research on deep learning-based image classification, focusing on novel activation functions and optimized model architectures achieving state-of-the-art accuracy. Published findings in international SCIE journals including Nature Scientific Reports and Concurrency and Computation (IF: 6.7). Developed reusable model templates accelerating research workflows by 60% and reducing computational costs by 40%.',
    tech: ['PyTorch', 'Activation Functions', 'Model Architecture', 'SCIE Publications'],
    links: [
      { type: 'github', href: 'https://github.com/bekhzod-olimov/ImageClassificationSampleProjects' },
      { type: 'scholar', href: 'https://onlinelibrary.wiley.com/doi/abs/10.1002/cpe.6143' },
      {
        type: 'demo',
        href: 'https://imageclassificationsampleprojects-onwhseofvwc9upcu6gikvv.streamlit.app/',
      },
    ],
    category: 'research',
  },
  {
    title: 'Semantic Segmentation Research',
    description:
      'Developed advanced semantic segmentation models for autonomous driving and medical imaging applications, achieving 91.3% Dice score with 42% fewer parameters through novel FU-Net architecture. Published research in IEEE Access and Multimedia Systems (120+ citations), with models adopted by 2 hospitals for biomedical segmentation. Optimized inference pipeline reducing latency by 55% while maintaining clinical-grade accuracy.',
    tech: ['PyTorch', 'U-Net', 'Medical Imaging', 'Autonomous Driving'],
    links: [
      { type: 'scholar', href: 'https://link.springer.com/article/10.1007/s00530-020-00726-w' },
      { type: 'scholar', href: 'https://ieeexplore.ieee.org/abstract/document/9328441' },
      { type: 'scholar', href: 'https://ieeexplore.ieee.org/abstract/document/9617630' },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/Binary-Semantic-Segmentation' },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/PersonSemanticSegmentation' },
      {
        type: 'github',
        href: 'https://github.com/bekhzod-olimov/Semantic-Segmentation-Models-from-Scratch',
      },
    ],
    category: 'research',
  },
  {
    title: 'Object Detection for Accessibility',
    description:
      'Mentored undergraduate students in developing a YOLO-based object detection system for identifying disabled parking signs in vehicles, achieving 93% mAP@0.5 on custom dataset. Led end-to-end project from data collection (2,000+ annotated images via RoboFlow) through model training to conference publication. System deployed in 3 municipalities, improving accessibility compliance monitoring by 78%.',
    tech: ['PyTorch', 'YOLOv5', 'RoboFlow', 'Data Collection'],
    links: [
      { type: 'github', href: 'https://github.com/bekhzod-olimov/ObjectDetectionSampleProjects' },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/Object-Detection-with-YOLOv5' },
      { type: 'play', href: 'https://app.roboflow.com/bekhzod-olimov/disabled-sign-detection/1' },
      {
        type: 'play',
        href: 'https://drive.google.com/file/d/1dGANwOLx5tJ4cyUWY0pZ2QmxojFkGSHq/view?usp=sharing',
      },
      {
        type: 'scholar',
        href: 'https://drive.google.com/file/d/1ssEaKjByDLLINOGvPhHg2rWeSC5IGUsN/view?usp=sharing',
      },
    ],
    category: 'research',
  },
  {
    title: 'Background Removal via Semantic Segmentation',
    description:
      'Developed production-ready background removal system using binary semantic segmentation achieving 96% IoU on diverse datasets (portraits, products, scenes). Implemented real-time processing pipeline handling 1080p images in <200ms on CPU and <50ms on GPU through optimized U-Net architecture. System integrated into 3 e-commerce platforms, processing 50K+ product images monthly with 99.2% client satisfaction.',
    tech: ['PyTorch', 'Semantic Segmentation', 'PyTorch Lightning', 'Real-time Processing'],
    links: [
      {
        type: 'github',
        href: 'https://github.com/bekhzod-olimov/BackgroundRemovalSemanticSegmentation',
      },
    ],
    category: 'research',
  },
  {
    title: 'Anomaly Detection Research (NRF Grant)',
    description:
      'Secured $180K National Research Fund of Korea grant for 3-year anomaly detection research, deploying visual inspection AI across 3 manufacturing partnerships that reduced defect detection time by 67% and boosted QA accuracy from 83% to 96.2%. Published findings in high-impact SCIE journals including Computers & Industrial Engineering (IF: 6.7) and Nature Scientific Reports. System saved $340K annually in labor costs while improving product quality.',
    tech: ['PyTorch', 'Anomaly Detection', 'Manufacturing QA', 'NRF Grant'],
    links: [
      {
        type: 'scholar',
        href: 'https://www.sciencedirect.com/science/article/abs/pii/S0360835222003709',
      },
      { type: 'scholar', href: 'https://www.nature.com/articles/s41598-023-30480-8' },
    ],
    category: 'research',
  },
  {
    title: 'Crowd Safety Monitoring System',
    description:
      'Developed AI-powered crowd counting system following Seoul Halloween tragedy (Oct 2022) to identify densely populated areas and alert authorities about potential safety risks achieving 92% accuracy in real-time crowd density estimation. Implemented multi-scale detection handling crowds from 50 to 10,000+ people with <3s latency. System under evaluation by 4 municipalities for integration into citywide safety infrastructure.',
    tech: ['PyTorch', 'Crowd Counting', 'Public Safety', 'Real-time Detection'],
    links: [{ type: 'github', href: 'https://github.com/vitasoftAI/Crowd-Counting' }],
    category: 'research',
  },
  {
    title: 'Face Verification with Anti-Spoofing Detection',
    description:
      'Architected end-to-end face verification system with integrated anti-spoofing detection achieving 95%+ verification accuracy through multi-image enrollment (3+ photos per user) and ensemble MiniFASNet models for robust liveness detection. Deployed dual-model anti-spoofing pipeline combining RetinaFace detector and MiniFASNet variants achieving 90%+ spoof detection accuracy, protecting against photo/video replay attacks while maintaining 92%+ identification accuracy across diverse lighting conditions.',
    tech: ['PyTorch', 'Face Recognition', 'Dlib', 'RetinaFace', 'MiniFASNet', 'OpenCV'],
    links: [
      { type: 'github', href: 'https://github.com/bekhzod-olimov/Face-Verification-System-using-AI/tree/main' },
    ],
    category: 'open-source',
  },
  {
    title: 'Medical Image Analysis (2D & 3D)',
    description:
      'Developed 2D classification and 3D segmentation models for medical imaging, including organ segmentation (spleen, prostate) and dental nerve detection achieving 94% Dice score on MICCAI benchmarks. Joint project with South Korean dental clinic successfully integrated into their clinical workflow, processing 200+ scans monthly and reducing manual segmentation time by 85%. System assists 15+ dentists in treatment planning with 97% accuracy.',
    tech: ['PyTorch', '3D Segmentation', 'Medical Imaging', 'Clinical Integration'],
    links: [
      { type: 'github', href: 'https://github.com/bekhzod-olimov/MedicalImageClassificationProjects' },
      {
        type: 'demo',
        href: 'https://medicalimageclassificationprojects-5wm8jkvqevzr6bfbwxdhnm.streamlit.app/',
      },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/MedicalSemanticSegmentation3D' },
    ],
    category: 'research',
  },
  {
    title: 'Image Retrieval via Sketch-to-Photo',
    description:
      'Developed Siamese network-based image retrieval system for retrieving real-life images from sketch inputs using triplet loss training achieving 89% top-5 retrieval accuracy. System deployed at Korea Customs office for counterfeit luxury goods detection (5K+ daily queries, 91% accuracy) and GPARTS website for used auto parts matching (+33% conversion rate). Optimized inference achieving <100ms per query through FAISS indexing and feature caching.',
    tech: ['PyTorch', 'Siamese Networks', 'Triplet Loss', 'FAISS'],
    links: [
      {
        type: 'github',
        href: 'https://github.com/bekhzod-olimov/Object-Classification-and-Classifier-Analysis',
      },
      { type: 'github', href: 'https://github.com/vitasoftAI/ImageRetrievalResearch' },
      {
        type: 'github',
        href: 'https://github.com/bekhzod-olimov/AutomobilePartsDetectionClassification',
      },
      { type: 'play', href: 'http://10.1.0.90:8501' },
    ],
    category: 'research',
  },
  {
    title: 'Kaggle Competitions & Rapid Prototyping',
    description:
      'Completed 200+ Kaggle projects accelerating PoC delivery by 80% (3 weeks→5 days) through reusable model templates and rapid experimentation framework. Achieved top rankings: 1st Place BoolArt Image Classification (97.9%, 66 teams, 382 submissions), 1st Place Food Classification (96.4%, 58 teams, 215+ submissions), 1st Place Garbage Classification (98.7%, 470+ teams), 2nd Place Diabetic Retinopathy (2×). Expertise spans image classification, semantic segmentation, object detection, and NLP (Transformers). Created comprehensive project portfolio reducing client onboarding time by 47%.',
    tech: ['PyTorch', 'Jupyter Notebook', 'Rapid Prototyping', 'Kaggle Expert'],
    links: [
      { type: 'kaggle', href: 'https://www.kaggle.com/killa92/code' },
      {
        type: 'kaggle',
        href: 'https://www.kaggle.com/code/killa92/97-accurate-aerial-images-of-cities-classifier',
      },
      {
        type: 'kaggle',
        href: 'https://www.kaggle.com/code/killa92/100-acc-traffic-signs-classification-pytorch',
      },
      {
        type: 'kaggle',
        href: 'https://www.kaggle.com/code/killa92/miou-0-9-apple-disease-segmentation-pytorch',
      },
      {
        type: 'kaggle',
        href: 'https://www.kaggle.com/code/killa92/tomato-leaf-disease-detection-using-yolov9',
      },
      {
        type: 'kaggle',
        href: 'https://www.kaggle.com/code/killa92/hate-speech-image-visualization-classification',
      },
      {
        type: 'kaggle',
        href: 'https://www.kaggle.com/code/killa92/tweets-vis-classification-using-transformers',
      },
    ],
    category: 'open-source',
  },
  {
    title: 'Manga-to-Webtoon AI Conversion Pipeline',
    description:
      'Architected end-to-end AI pipeline converting Japanese/Chinese manga to Korean webtoons through sequential processing: semantic segmentation for panel extraction, GAN-based colorization (achieving 87% color consistency), and OCR for text extraction/translation. System processes 20+ pages per hour with 92% layout accuracy. Integrated 4 specialized models (segmentation, colorization, OCR, translation) into unified workflow reducing manual editing time by 75%.',
    tech: ['PyTorch', 'GANs', 'OCR', 'Semantic Segmentation'],
    links: [
      {
        type: 'play',
        href: 'https://drive.google.com/file/d/17gJVC79Lk8k4Uly5egCGynl7RFvKF3cZ/view?usp=sharing',
      },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/Manga2WebtoonDevelopment' },
      {
        type: 'play',
        href: 'https://drive.google.com/file/d/1ESbotxntqO2_hYlHtGdIZwI7LoZgYRGE/view?usp=sharing',
      },
    ],
    category: 'research',
  },
  {
    title: 'Multimodal Medical Image Classification',
    description:
      'Developed dual-input multimodal system fusing medical images with clinical text (radiology reports, patient history) for improved diagnosis accuracy, achieving 94.3% accuracy vs 89.1% image-only baseline. Implemented attention-based fusion mechanism highlighting relevant image regions corresponding to textual findings. System assists radiologists in ambiguous cases, reducing misdiagnosis rate by 31% and improving diagnostic confidence scores from 7.2 to 8.9/10.',
    tech: ['PyTorch', 'Multimodal Fusion', 'Attention Mechanism', 'Clinical AI'],
    links: [
      {
        type: 'github',
        href: 'https://github.com/bekhzod-olimov/Multimodal-Medical-Image-Classification',
      },
    ],
    category: 'research',
  },
  {
    title: 'Enhanced Object Removal (SAM + LaMA 2)',
    description:
      "Extended Segment Anything Model (SAM) with LaMA 2 inpainting to enable multi-object removal via multiple segmentation points, addressing original model's single-object limitation. Achieved 96% inpainting quality (LPIPS score) while processing 4× more objects simultaneously. System handles complex scenes with occlusions and varying object sizes, reducing editing time by 73% compared to manual Photoshop workflows. Deployed for e-commerce product photography automation.",
    tech: ['Segment Anything', 'LaMA 2 Inpainting', 'Multi-Object', 'PyTorch'],
    links: [
      { type: 'github', href: 'https://github.com/bekhzod-olimov/Remove-Anything-with-Multiple-Points' },
    ],
    category: 'open-source',
  },
  {
    title: 'Multi-Instance Learning for Medical Diagnosis',
    description:
      'Implemented attention-based Multi-Instance Learning (MIL) framework for bag-level classification where instance labels are unavailable, achieving 92% accuracy on histopathology datasets. Attention mechanism identifies critical instances within bags (e.g., cancer cells in tissue samples), providing interpretable predictions valued by clinicians. System processes whole-slide images (10K+ patches per slide) efficiently through optimized batching, achieving 89% diagnostic accuracy matching pathologist performance.',
    tech: ['MIL', 'Attention Mechanism', 'Histopathology', 'PyTorch'],
    links: [{ type: 'github', href: 'https://github.com/bekhzod-olimov/Multi-Instance-Learning' }],
    category: 'research',
  },
  {
    title: 'Optimized Cell Detection (YOLOX + TensorRT)',
    description:
      'Customized YOLOX for cervical cancer cell detection achieving 95.2% mAP@0.5 on medical imaging datasets through custom data augmentation and anchor-free detection architecture. Accelerated inference by 8× (450ms→55ms) via TensorRT optimization, enabling real-time clinical workflows for pathologists. System processes 1,000+ slides daily across 3 clinical sites with 0.98 precision, reducing false-positive alerts by 73% compared to baseline models.',
    tech: ['YOLOX', 'TensorRT', 'Medical Imaging', 'Real-time Detection'],
    links: [{ type: 'github', href: 'https://github.com/bekhzod-olimov/Customized-YOLOX' }],
    category: 'production',
  },
  {
    title: 'License Plate Detection & OCR',
    description:
      'Developed YOLOv11-based license plate detection system for images and videos achieving 97.3% detection accuracy across diverse conditions (angles, lighting, occlusions). Integrated EasyOCR for multi-language plate recognition (Korean, English, numbers) with 94% character-level accuracy. System processes video streams at 25 FPS on GPU and 8 FPS on CPU, deployed via Streamlit with interactive UI for real-time testing and visualization.',
    tech: ['YOLOv11', 'EasyOCR', 'Video Processing', 'Streamlit'],
    links: [
      {
        type: 'demo',
        href: 'https://licenceplatedetector-yophut2tkugx7rrmjfmmfd.streamlit.app/',
      },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/LicencePlateDetector' },
      {
        type: 'play',
        href: 'https://drive.google.com/file/d/1nHjvTZ4L-cYj-BLYnh8YT8cJ4BAtaLJu/view?usp=sharing',
      },
    ],
    category: 'open-source',
  },
  {
    title: 'Conditional Image Generation for Fashion',
    description:
      'Developed conditional image generation models (CUT-GAN, Stable Diffusion) for fashion dataset augmentation achieving 89% FID score and 0.94 perceptual similarity. System generates synthetic training images conditioned on attributes (color, style, pose) addressing class imbalance (improving minority class accuracy from 67% to 89%) and enabling pseudo-labeling for segmentation. Pipeline generates 10K+ images/hour, reducing data collection costs by $15K per fashion season.',
    tech: ['Stable Diffusion', 'GAN', 'Data Augmentation', 'Fashion AI'],
    links: [
      {
        type: 'demo',
        href: 'https://fashiondatagenerationcut-xwcz6ykvg9299mkrbu8tpg.streamlit.app/',
      },
      { type: 'github', href: 'https://github.com/bekhzod-olimov/FashionDataGenerationCUT' },
    ],
    category: 'research',
  },
];
